import { useEffect, useState } from 'react';
import { API_BASE } from '../lib/api';

/**
 * /baja/<token> — página PÚBLICA de baja de comunicados (V92). Fuera de
 * AuthProvider (patrón /legal y /reservar): sin sesión; el token del correo es
 * la capability. El GET solo INFORMA y la baja se ejecuta con el botón (POST)
 * — un GET que diera de baja se lo comerían los prefetchers de Gmail/antivirus.
 * La baja es de ESE negocio: no afecta otros estudios ni los correos
 * transaccionales (citas/reservas).
 */

const PATH_RE = /^\/baja\/([A-Za-z0-9]{16,128})\/?$/;

export const isUnsubscribeRoute = (pathname: string): boolean => PATH_RE.test(pathname);

export function UnsubscribePage() {
  const token = PATH_RE.exec(window.location.pathname)?.[1] ?? '';
  const [info, setInfo] = useState<{ businessName: string; email: string; alreadySuppressed: boolean } | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'done' | 'error'>('loading');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/public/comm/unsubscribe/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error();
        const data = await r.json();
        setInfo(data);
        setState(data.alreadySuppressed ? 'done' : 'ready');
      })
      .catch(() => setState('error'));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/public/comm/unsubscribe/${token}`, { method: 'POST' });
      if (!r.ok) throw new Error();
      setState('done');
    } catch {
      setState('error');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4">
      <div className="card-soft w-full max-w-sm rounded-2xl border border-slate-200/80 bg-white p-8 text-center">
        {state === 'loading' && <p className="text-sm text-slate-500">Cargando…</p>}

        {state === 'error' && (
          <>
            <h1 className="font-serif text-xl text-slate-900">Enlace inválido</h1>
            <p className="mt-2 text-sm text-slate-500">
              Este enlace de baja no es válido o ya venció. Si quieres dejar de recibir
              comunicados, responde al correo del negocio pidiéndolo.
            </p>
          </>
        )}

        {state === 'ready' && info && (
          <>
            <h1 className="font-serif text-xl text-slate-900">Darte de baja</h1>
            <p className="mt-2 text-sm text-slate-600">
              Dejarás de recibir comunicados de <b>{info.businessName}</b> en{' '}
              <b>{info.email}</b>.
            </p>
            <p className="mt-2 text-xs text-slate-400">
              Los correos de tus citas y reservas (confirmaciones, cambios,
              cancelaciones) seguirán llegando normalmente. La baja aplica solo a
              este negocio.
            </p>
            <button
              onClick={confirm}
              disabled={busy}
              className="mt-5 w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? 'Procesando…' : 'Confirmar baja'}
            </button>
          </>
        )}

        {state === 'done' && info && (
          <>
            <h1 className="font-serif text-xl text-slate-900">Listo ✓</h1>
            <p className="mt-2 text-sm text-slate-600">
              <b>{info.email}</b> ya no recibirá comunicados de{' '}
              <b>{info.businessName}</b>.
            </p>
            <p className="mt-2 text-xs text-slate-400">
              Si cambias de opinión, avísale directamente al negocio para que te
              re-suscriba.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
